import { QueryFunctionContext, QueryKey, useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';
import { Compulsory } from 'ts-toolbelt/out/Object/Compulsory';
import { LimitedSchemaInfiniteQueryOptions } from '@frontend/react-query-helpers';
import { SchemaDigitalForms } from '../../service';
import { IO } from '../../types';
import { defaultOptions } from '../config';
import { requestKeys } from '../keys';

/**
 * @deprecated Use `GetSubmissionRequest` from Schema instead
 */
export interface SubmissionListRequestParams {
  page?: number;
  limit?: number;
  solicited?: boolean;
  submitted?: boolean;
  submitted_from?: number;
  submitted_till?: number;
  submission_category?: 'completed' | 'retry_sync' | 'needs_review' | 'sync_manually' | 'reviewed';
  search?: string;
  order?: 'desc' | 'asc';
  sortBy?: 'submitted_at' | 'created_at' | 'archived_at';
  is_archive?: boolean;
  provider_email?: string;
  review_status?: 'pending' | 'approved' | 'rejected' | 'reviewed';
  company_ids?: string[];
}

/**
 * @deprecated Use `SubmissionListV2Response` instead.
 */
export interface SubmissionListResponse {
  rows: IO.GetSubmissionsList['output']['data'];
  meta: IO.GetSubmissionsList['output']['meta'];
}

/**
 * @deprecated Use `useGetListOfSubmissionsV2` instead.
 */
export const useGetListOfSubmissions = <E = unknown, T = SubmissionListResponse>(
  req: Compulsory<IO.GetSubmissionsList['input'], 'companyIds'>,
  params: SubmissionListRequestParams,
  options?: LimitedSchemaInfiniteQueryOptions<IO.GetSubmissionsList, E, T>
) => {
  const queryKey = requestKeys.submissionsList({ ...req, ...params });
  const query = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }: QueryFunctionContext<string | readonly unknown[], SubmissionListRequestParams>) => {
      const response = await SchemaDigitalForms.GetSubmissions(req, {
        params: {
          ...params,
          page: pageParam?.page,
        },
      });

      return {
        rows: response.data || [],
        meta: response.meta,
      };
    },
    getNextPageParam: ({ meta }) => {
      if (!meta?.nextPage) {
        return undefined;
      }
      return { page: meta.nextPage };
    },
    ...defaultOptions,
    ...options,
  });

  return {
    queryKey,
    ...query,
  };
};

export interface SubmissionListV2Response {
  rows: IO.GetSubmissionsListV2['output']['data'];
  meta: IO.GetSubmissionsListV2['output']['meta'];
}

type UseGetListOfSubmissionsV2Results<T = SubmissionListV2Response, E = unknown> = UseInfiniteQueryResult<T, E> & {
  queryKey: QueryKey;
};

export const useGetListOfSubmissionsV2 = <E = unknown, T = SubmissionListV2Response>(
  req: IO.GetSubmissionsListV2['input'],
  options?: LimitedSchemaInfiniteQueryOptions<IO.GetSubmissionsListV2, E, T>
): UseGetListOfSubmissionsV2Results<T, E> => {
  const queryKey = requestKeys.submissionsList(req);
  const query = useInfiniteQuery({
    queryKey,
    queryFn: async ({
      pageParam,
    }: QueryFunctionContext<QueryKey, IO.GetSubmissionsListV2['input']>): Promise<SubmissionListV2Response> => {
      const nextPage = pageParam?.pagination?.page;
      const response = await SchemaDigitalForms.GetSubmissionsV2({
        ...req,
        pagination: {
          ...req.pagination,
          page: nextPage ? nextPage : req.pagination?.page,
        },
      });

      return {
        rows: response.data || [],
        meta: response.meta,
      };
    },
    getNextPageParam: ({ meta }): Partial<IO.GetSubmissionsListV2['input']> | undefined => {
      const nextPage = meta?.nextPage;
      return !!nextPage ? { pagination: { page: nextPage } } : undefined;
    },
    ...defaultOptions,
    ...options,
    retry: false,
  });

  return {
    queryKey,
    ...query,
  };
};
